/**
 * Identidad de una tarjeta que no es Shelly.
 * Un poll de estado (socket o refresco cada 5s) no puede sacar la tarjeta de la
 * habitación: habitación, nombre, tipo y enabled solo se pisan con un dato concreto.
 */

const IDENTITY_KEYS = new Set([
  'id',
  'habitacion_id',
  'enabled',
  'nombre',
  'orden',
  'device_kind',
  'ui_family',
  'artefacto',
  'artefacto_id',
  'paired',
]);

function habitacionConcreta(value: unknown): number | null {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value === 'string' && value !== '' && Number.isFinite(Number(value))) {
    return Number(value);
  }
  return null;
}

/**
 * Fusiona un estado en vivo sobre la tarjeta que ya está en memoria.
 * Si todavía no está y el payload trae nombre + habitación, la vuelve a crear
 * (el GET de refresh de la habitación alcanza para recuperarla).
 * Devuelve null si no hay nada que guardar.
 */
export function patchRoomCardLive<T extends { id: number }>(
  prev: T | undefined,
  incoming: Partial<T> & { id?: number },
): T | null {
  if (incoming?.id == null) return prev ?? null;
  const id = Number(incoming.id);
  if (!Number.isFinite(id)) return prev ?? null;

  const incomingRec = incoming as Record<string, unknown>;
  if (!prev) {
    const habitacion = habitacionConcreta(incomingRec.habitacion_id);
    const nombre = incomingRec.nombre;
    if (habitacion == null || typeof nombre !== 'string' || nombre === '') return null;
    return { ...incoming, id, habitacion_id: habitacion } as unknown as T;
  }

  const next = { ...prev, id } as T;
  const nextRec = next as Record<string, unknown>;
  Object.keys(incomingRec).forEach((key) => {
    if (IDENTITY_KEYS.has(key)) return;
    const value = incomingRec[key];
    if (value !== undefined && value !== null) nextRec[key] = value;
  });

  const habitacion = habitacionConcreta(incomingRec.habitacion_id);
  if (habitacion != null) nextRec.habitacion_id = habitacion;
  if (incomingRec.enabled === true) nextRec.enabled = true;
  if (incomingRec.device_kind === 'ac' || incomingRec.device_kind === 'tv') {
    nextRec.device_kind = incomingRec.device_kind;
  }
  if (typeof incomingRec.ui_family === 'string' && incomingRec.ui_family) {
    nextRec.ui_family = incomingRec.ui_family;
  }
  return next;
}
